import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box, Paper, Typography, Switch, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Chip,
  TextField, TablePagination, InputAdornment
} from '@mui/material';
import { SearchOutlined } from '@mui/icons-material';
import { updateScreenTitle } from '../../../LayoutContainers/AppLayout/redux/AppSlice';
import AgentService from '../../../services/AgentService';

const AgentConfig = () => {
  const dispatch = useDispatch();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(updateScreenTitle('AI Agent Configuration'));
    loadData();
    return () => dispatch(updateScreenTitle(''));
  }, [dispatch]);

  const loadData = async () => {
    try {
      const data = await AgentService.listOrganizations();
      setOrgs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (orgId, currentEnabled) => {
    setUpdating(orgId);
    try {
      await AgentService.updateOrgConfig(orgId, { enabled: !currentEnabled });
      setOrgs(prev => prev.map(o =>
        o.organizationId === orgId ? { ...o, enabled: !currentEnabled } : o
      ));
    } catch (error) {
      console.error('Failed to update config:', error);
    } finally {
      setUpdating(null);
    }
  };

  const filteredOrgs = orgs.filter(o =>
    !search || (o.organizationName || '').toLowerCase().includes(search.toLowerCase())
  );

  const paginatedOrgs = filteredOrgs.slice(
    page * rowsPerPage, page * rowsPerPage + rowsPerPage
  );

  const enabledCount = orgs.filter(o => o.enabled).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={600} fontSize="1rem">
              AI Agent — Per Organization
            </Typography>
            <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
              {orgs.length} organizations | {enabledCount} enabled
            </Typography>
          </Box>
          <TextField
            size="small"
            placeholder="Search organization..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ width: 250, '& .MuiOutlinedInput-root': { fontSize: '0.825rem' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', bgcolor: 'grey.50' }}>
                  Organization
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', bgcolor: 'grey.50', width: 100 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', bgcolor: 'grey.50', width: 180 }}>
                  Model
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem', bgcolor: 'grey.50', width: 120 }}>
                  Enable/Disable
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedOrgs.map(org => (
                <TableRow key={org.organizationId} hover>
                  <TableCell sx={{ fontSize: '0.825rem' }}>{org.organizationName}</TableCell>
                  <TableCell>
                    <Chip
                      label={org.enabled ? 'Enabled' : 'Disabled'}
                      color={org.enabled ? 'success' : 'default'}
                      size="small"
                      variant="filled"
                      sx={{ fontSize: '0.7rem', height: 22 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                      {(org.model || 'claude-sonnet-4').replace('claude-sonnet-4-20250514', 'Sonnet 4')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {updating === org.organizationId ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Switch
                        checked={org.enabled === true}
                        onChange={() => handleToggle(org.organizationId, org.enabled)}
                        color="primary"
                        size="small"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {paginatedOrgs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary" fontSize="0.825rem">
                      {search ? 'No organizations match your search' : 'No customer organizations found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredOrgs.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
        />
      </Paper>
    </Box>
  );
};

export default AgentConfig;
